/**
 * Operate (docs/plans/2026-10-cloud-licences-and-operations.md, section 2): Mojo Up AI Cloud as a
 * whole, for Mojo Up's own operators. Health from the status page's last round, load as the
 * platform itself can count it, storage against the ceilings and the database's disk, then every
 * organisation with what pays for it. Reading a customer's organisation goes through a grant on
 * Platform access; the one thing operators change from here is a removal, with a reason and the
 * name typed back, which the people concerned are told about.
 *
 * CPU, replicas and database connections are the cloud provider's meters (Azure Monitor alerts go
 * to the customer portal), not this page's.
 */

import { quotaAmountText, type OperatorOrganisationRow, type OrganisationPlanSource, type StatusState } from '@aioe/shared';
import { Database, Display, HeartPulse, Magnifier, Persons, Speedometer } from '@gravity-ui/icons';
import { Button, Input, Label, TextArea, TextField } from '@heroui/react';
import { useState, type ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { operatorAccessRoutes, operatorRoutes, problemText } from '../api/client.ts';
import { FormModal } from '../components/cloud/FormModal.tsx';
import { OpenAccessModal, StaffAccessGrants } from '../components/cloud/StaffAccessGrants.tsx';
import { ArtEmpty, ChoiceSelect, ErrorNote, ListBlock, ListGroup, ListRow, Page, PageHeader, Stat, StatusMark } from '../components/ui.tsx';
import { RowMenu } from '../components/ui/RowMenu.tsx';
import { SearchBox } from '../components/ui/SearchBox.tsx';
import type { ArtTone } from '../components/ui/Art.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';
import { isCloud } from '../edition.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { listCount, NoMatch, ShowMore, useListSearch, usePagedList } from '../pagedList.tsx';

const tone: ArtTone = 'slate';
const bytes = (n: number) => quotaAmountText('bytes', n);
const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString('en-AU')} ${n === 1 ? one : many}`;

const HEALTH: Record<StatusState, { state: 'online' | 'medium' | 'error'; label: string; short: string }> = {
  ok: { state: 'online', label: 'All systems working', short: 'Working' },
  degraded: { state: 'medium', label: 'Some systems degraded', short: 'Degraded' },
  down: { state: 'error', label: 'Major outage', short: 'Outage' },
};

const SOURCE_WORDS: Record<OrganisationPlanSource, string> = { subscription: 'Teams, paid', trial: 'Teams, on trial', licence: 'Teams, licensed', none: 'Free' };

export function OperatePage() {
  if (!isCloud()) return <Navigate to="/" replace />;
  return <OperateView />;
}

function OperateView() {
  const navigate = useNavigate();
  const grants = useLoad(() => operatorAccessRoutes.grants(), [], 60_000);
  const operator = grants.data?.mayOpen;
  const overview = useLoad(() => (operator ? operatorRoutes.overview() : Promise.resolve(undefined)), [operator], 60_000);
  // Searched and filtered on the server, a page at a time.
  const search = useListSearch();
  const [plan, setPlan] = useState('all');
  const [source, setSource] = useState('all');
  const query = { q: search.q, plan: plan === 'all' ? undefined : plan, source: source === 'all' ? undefined : source, operator };
  const list = usePagedList(
    (cursor) => (operator ? operatorRoutes.organisationsPage({ ...query, cursor }) : Promise.resolve({ items: [] as OperatorOrganisationRow[], next: undefined, extra: { computedAt: '', total: 0, matching: 0 } })),
    query,
    { intervalMs: 60_000, keyOf: (r) => r.id },
  );
  const narrowed = search.q !== '' || plan !== 'all' || source !== 'all';
  const [removing, setRemoving] = useState<{ kind: 'organisation'; row: OperatorOrganisationRow } | { kind: 'person' }>();
  // Opening staff access: to one organisation (from its row) or to one named in the form.
  const [opening, setOpening] = useState<{ organisationId?: string }>();

  if (grants.data && !operator) {
    return (
      <Page>
        <PageHeader tone={tone} icon={<Speedometer />} title="Operate" subtitle="Mojo Up AI Cloud as a whole, for Mojo Up's operators." />
        <ArtEmpty tone={tone} icon={<Speedometer />} title="For Mojo Up's operators">
          {grants.data.why ?? 'Only the platform operator’s administrators see this page.'}
        </ArtEmpty>
      </Page>
    );
  }
  const o = overview.data;
  const rows = list.items;
  const health = o ? HEALTH[o.health.overall] : undefined;
  const reload = () => {
    overview.reload();
    list.reload();
  };

  return (
    <Page>
      <PageHeader
        tone={tone}
        icon={<Speedometer />}
        title="Operate"
        subtitle="Mojo Up AI Cloud as a whole: health, load, storage, and every organisation with what pays for it. Reading one organisation needs staff access opened first, which the organisation sees."
        actions={
          <Button size="sm" variant="secondary" onPress={() => setRemoving({ kind: 'person' })}>
            Remove a person
          </Button>
        }
      />
      <PartBoundary key="stats" name="Overview" tone={tone}>
        <div className="art-stats">
          <Stat tone={tone} icon={<HeartPulse />} label="Health" value={health?.short} detail={o ? (o.health.openIncidents ? plural(o.health.openIncidents, 'open incident') : health?.label === 'All systems working' ? (o.health.checkedAt ? `All working, checked ${timeAgo(o.health.checkedAt)}` : 'Not checked yet') : `${health?.label}; see Status`) : ''} needsYou={!!o && o.health.overall !== 'ok'} onPress={() => navigate('/status')} />
          <Stat tone={tone} icon={<Persons />} label="Organisations" value={o?.load.organisations} detail={o ? `${plural(o.load.teams, 'team')}, ${plural(o.load.personal, 'personal space')}; ${o.load.organisationsToday} made today` : ''} />
          <Stat tone={tone} icon={<Persons />} label="People" value={o?.load.people} detail={o && o.load.peopleSeenToday !== undefined ? `${o.load.peopleSeenToday} seen today` : ''} />
          <Stat tone={tone} icon={<Display />} label="Machines" value={o?.load.machines} detail={o ? `${o.load.machinesSeenThisHour} reported this hour` : ''} />
          <Stat tone={tone} icon={<Database />} label="Stored" value={o ? bytes(o.storage.organisationsBytes) : undefined} detail={o ? (o.storage.measuredAt ? `Across every organisation, counted ${timeAgo(o.storage.measuredAt)}` : 'Not counted yet') : ''} />
          <Stat
            tone={tone}
            icon={<Database />}
            label="Database"
            value={o ? (o.storage.databaseBytes !== undefined ? bytes(o.storage.databaseBytes) : '—') : undefined}
            detail={o ? (o.storage.databaseAllocatedBytes ? `of ${bytes(o.storage.databaseAllocatedBytes)} of disk` : 'On disk; the disk given is not set') : ''}
            needsYou={!!o && !!o.storage.databaseBytes && !!o.storage.databaseAllocatedBytes && o.storage.databaseBytes >= o.storage.databaseAllocatedBytes * 0.8}
          />
        </div>
        <ErrorNote error={grants.error ?? overview.error ?? list.error} />
      </PartBoundary>
      <PartBoundary key="health" name="Health and capacity" tone={tone}>
        {o ? (
          <div className="grid items-start gap-4 @[60rem]:grid-cols-2">
            <ListGroup title="Components" description={`What the public status page shows; ${o.version} is running.`} empty={o.health.components.length === 0 ? <ArtEmpty tone={tone} icon={<HeartPulse />} compact title="Not checked yet">The first round of checks has not run yet.</ArtEmpty> : undefined}>
              {o.health.components.map((c) => (
                <ListRow key={c.id} title={c.name} description={c.message} status={<StatusMark state={HEALTH[c.state].state} label={c.state === 'ok' ? 'Working' : c.state === 'degraded' ? 'Degraded' : 'Down'} />} />
              ))}
            </ListGroup>
            <ListGroup title="Ceilings" description={o.capacity.configured ? (o.capacity.refusing ? 'A ceiling is crossed: new organisations and Free growth are refused.' : "The deployment's own bounds (AIOE_PLATFORM_CEILINGS).") : 'No ceilings are set (AIOE_PLATFORM_CEILINGS).'}>
              {o.capacity.lines
                // Mojo Up AI Cloud offers no mesh, so its ceiling is not a line here.
                .filter((l) => l.key !== 'meshNodes')
                .map((l) => (
                  <ListRow
                    key={l.key}
                    title={l.label}
                    description={
                      <span className="tabular-nums">
                        {l.key === 'storageBytes' ? bytes(l.used) : l.used.toLocaleString('en-AU')}
                        {l.ceiling === null ? ', no ceiling set' : ` of ${l.key === 'storageBytes' ? bytes(l.ceiling) : l.ceiling.toLocaleString('en-AU')}`}
                      </span>
                    }
                    status={<StatusMark state={l.state === 'ok' ? 'online' : l.state === 'near' ? 'medium' : 'error'} label={l.state === 'ok' ? 'Room' : l.state === 'near' ? 'Near the ceiling' : 'Crossed'} />}
                  />
                ))}
              <ListBlock className="text-xs text-muted">
                Teams on Teams: {o.load.teamsOnTeams.subscription} paying, {o.load.teamsOnTeams.trial} on trial, {o.load.teamsOnTeams.licence} licensed. CPU, replicas and database connections are in Azure Monitor; its alerts go to the customer portal.
              </ListBlock>
            </ListGroup>
          </div>
        ) : null}
      </PartBoundary>
      <PartBoundary key="staff-access" name="Staff access" tone={tone}>
        {operator ? <StaffAccessGrants tone={tone} grants={grants.data} onOpen={() => setOpening({})} onChanged={grants.reload} /> : null}
      </PartBoundary>
      <PartBoundary key="organisations" name="Organisations" tone={tone}>
        <ListGroup
          title="Organisations"
          count={list.loaded ? listCount(rows.length, { one: 'organisation', many: 'organisations' }, { total: narrowed ? undefined : list.extra?.total, hasMore: list.hasMore, searched: narrowed }) : undefined}
          description={list.extra?.computedAt ? `Newest first, as counted ${timeAgo(list.extra.computedAt)}.` : undefined}
          actions={
            <>
              <SearchBox label="Find an organisation" placeholder="Name, owner’s email or slug" value={search.typed} onChange={search.setTyped} className="w-40 max-w-full @lg:w-56" />
              <ChoiceSelect label="Plan" value={plan} options={{ all: 'Any plan', free: 'Free', teams: 'Teams' }} onChange={setPlan} className="w-fit min-w-32 max-w-full" />
              <ChoiceSelect label="State" value={source} options={{ all: 'Any state', subscription: 'Paid', trial: 'On trial', licence: 'Licensed', none: 'No subscription' }} onChange={setSource} className="w-fit min-w-36 max-w-full" />
            </>
          }
          empty={
            list.loaded && rows.length === 0 ? (
              narrowed ? (
                <NoMatch tone={tone} icon={<Magnifier />} q={search.q} filtered={plan !== 'all' || source !== 'all'} noun="organisations" onClear={() => (search.clear(), setPlan('all'), setSource('all'))} />
              ) : (
                <ArtEmpty tone={tone} icon={<Magnifier />} title="No organisations yet" compact>
                  Organisations appear here as people make them.
                </ArtEmpty>
              )
            ) : undefined
          }
        >
          {rows.map((r) => (
            <OrganisationRow key={r.id} row={r} onRemove={() => setRemoving({ kind: 'organisation', row: r })} onOpen={() => setOpening({ organisationId: r.id })} />
          ))}
          {list.hasMore ? <ShowMore onPress={list.showMore} pending={list.loadingMore} /> : null}
        </ListGroup>
      </PartBoundary>
      {removing?.kind === 'organisation' ? <RemoveOrganisationModal row={removing.row} onClose={() => setRemoving(undefined)} onDone={reload} /> : null}
      {removing?.kind === 'person' ? <RemovePersonModal onClose={() => setRemoving(undefined)} onDone={reload} /> : null}
      {opening ? (
        <OpenAccessModal
          {...opening}
          onClose={() => setOpening(undefined)}
          onDone={() => {
            setOpening(undefined);
            grants.reload();
          }}
        />
      ) : null}
    </Page>
  );
}

function OrganisationRow({ row: r, onRemove, onOpen }: { row: OperatorOrganisationRow; onRemove: () => void; onOpen: () => void }) {
  const mark: { state: 'connected' | 'online' | 'idle' | 'disabled'; label: string } =
    r.kind === 'enterprise'
      ? { state: 'connected', label: "The platform's own" }
      : r.operator
        ? { state: 'connected', label: "The operators'" }
        : r.plan === 'teams'
        ? { state: r.source === 'trial' ? 'online' : 'connected', label: SOURCE_WORDS[r.source] }
        : r.kind === 'personal'
          ? { state: 'idle', label: 'Personal' }
          : { state: 'disabled', label: r.status === 'none' ? 'Free' : `Free, ${r.status.replace('_', ' ')}` };
  const facts = [r.kind === 'team' ? `${plural(r.members, 'person', 'people')}${r.plan === 'teams' && !r.operator ? ` of ${r.seatsPurchased}` : ''}` : undefined, plural(r.machines, 'machine'), r.bytes !== undefined ? bytes(r.bytes) : undefined, r.lastSeenAt ? `seen ${timeAgo(r.lastSeenAt)}` : 'never seen'].filter(Boolean).join(' · ');
  const owners = (r.owners ?? []).map((p) => p.email ?? p.name).filter(Boolean);
  return (
    <ListRow
      icon={<Persons />}
      title={r.name}
      description={
        <>
          {owners.length ? <span className="block break-all">{owners.length > 2 ? `${owners.slice(0, 2).join(', ')} and ${owners.length - 2} more` : owners.join(', ')}</span> : null}
          <span className="block">
            {r.slug} · made {timeAgo(r.createdAt)}
          </span>
          <span className="block tabular-nums">{facts}</span>
        </>
      }
      status={<StatusMark state={mark.state} label={mark.label} />}
      compactControl
      control={
        <RowMenu
          label={`More for ${r.name}`}
          items={[
            { id: 'open', label: 'Open access to read it' },
            { id: 'remove', label: 'Remove the organisation…', danger: true },
          ]}
          onAction={(key) => (key === 'remove' ? onRemove() : onOpen())}
        />
      }
    />
  );
}

function Done({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) {
  return (
    <FormModal
      isOpen
      onClose={onClose}
      title={title}
      blurb={children}
      submitLabel="Done"
      onSubmit={onClose}
      footer={
        <Button variant="primary" onPress={onClose}>
          Done
        </Button>
      }
    />
  );
}

function RemoveOrganisationModal({ row, onClose, onDone }: { row: OperatorOrganisationRow; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<{ notified: number }>();
  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const r = await operatorRoutes.removeOrganisation({ organisationId: row.id, reason, confirm });
      setDone({ notified: r.notified });
      onDone();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  if (done) {
    return (
      <Done title={`${row.name} has been removed`} onClose={onClose}>
        Everything it held is gone. {done.notified ? `${plural(done.notified, 'person', 'people')} in it ${done.notified === 1 ? 'was' : 'were'} emailed the reason.` : 'Nobody in it had an email address to tell.'}
      </Done>
    );
  }
  return (
    <FormModal isOpen onClose={onClose} title={`Remove ${row.name}`} blurb={`Everything the organisation holds goes: ${plural(row.members, 'person', 'people')}' memberships, ${plural(row.machines, 'machine')}, projects, memory, backups and its record. Its people keep their accounts and are emailed the reason. This cannot be undone.`} submitLabel="Remove the organisation" canSubmit={reason.trim().length >= 10 && confirm.trim().toLowerCase() === row.name.toLowerCase()} busy={busy} error={error} onSubmit={() => void submit()}>
      <TextField variant="secondary" value={reason} onChange={setReason} isRequired autoFocus>
        <Label>Reason, in words the people will read</Label>
        <TextArea rows={3} placeholder="Abuse report 7731: the organisation was made to spam invitations." />
      </TextField>
      <TextField variant="secondary" value={confirm} onChange={setConfirm} isRequired>
        <Label>Type the organisation's name to confirm</Label>
        <Input placeholder={row.name} autoComplete="off" />
      </TextField>
    </FormModal>
  );
}

function RemovePersonModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<{ purged: string[]; left: string[] }>();
  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const r = await operatorRoutes.removePerson({ email, reason, confirm });
      setDone({ purged: r.purged.map((o) => o.name), left: r.left.map((o) => o.name) });
      onDone();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  if (done) {
    return (
      <Done title="The account has been removed" onClose={onClose}>
        {done.purged.length ? `Removed with it, since only they were in them: ${done.purged.join(', ')}. ` : ''}
        {done.left.length ? `Taken out of ${done.left.join(', ')}, which carry on. ` : ''}
        They were emailed the reason.
      </Done>
    );
  }
  return (
    <FormModal isOpen onClose={onClose} title="Remove a person" blurb="Their account, sign-ins and sessions go. Organisations only they were in go with them; the others carry on without them. The only owner of an organisation that others are in cannot be removed until that organisation is, or has another owner. They are emailed the reason. This cannot be undone." submitLabel="Remove the person" canSubmit={/\S+@\S+\.\S+/.test(email) && reason.trim().length >= 10 && confirm.trim().toLowerCase() === email.trim().toLowerCase()} busy={busy} error={error} onSubmit={() => void submit()}>
      <TextField variant="secondary" value={email} onChange={setEmail} isRequired autoFocus>
        <Label>Email address they have proved</Label>
        <Input type="email" placeholder="someone@example.com" autoComplete="off" />
      </TextField>
      <TextField variant="secondary" value={reason} onChange={setReason} isRequired>
        <Label>Reason, in words they will read</Label>
        <TextArea rows={3} placeholder="Request under the Privacy Act, ticket 4411." />
      </TextField>
      <TextField variant="secondary" value={confirm} onChange={setConfirm} isRequired>
        <Label>Type the address again to confirm</Label>
        <Input autoComplete="off" />
      </TextField>
    </FormModal>
  );
}
